<template>
  <messenger-chat-view-layout
    ref="viewRef"
    v-bind="dropZoneProps"
    :chat-state="chatState"
    :agent-typing-count="agentTypingCount"
    @back="$emit('back')"
    @end-chat="$emit('end-chat')"
  >
    <template v-slot:alert>
      <slot name="alert" />
    </template>

    <div class="chat-wrapper">
      <div class="chat-wrapper__chat-area">
        <div
          ref="viewScrollContainerRef"
          class="chat-wrapper__scroll-container"
          v-bind="{
            [scrollbarDataIdAttribute]: scrollBarId,
          }"
        >
          <div
            ref="viewScrolledContentRef"
            class="chat-wrapper__scrolled-content"
          >
            <messenger-chat-message-list
              :list-items="messageListItems"
              :list-state="messageListState"
              :is-agent-searching="isChatWaitingState(chatState)"
              :first-new-message-id="newMessageState?.firstNewMessageId"
              :new-message-divider-id="newMessageDividerId"
              :is-loading="isChatLoadingState(chatState) || messageListItems.length === 0"
              @load-next-page="$emit('load-next-page')"
            />
          </div>
        </div>

        <transition name="fade">
          <div
            v-if="isScrollButtonShown"
            class="chat-wrapper__scroll-button"
          >
            <widget-button
              v-bind="hasNotViewedNewMessages ? themeButtonProps : { color: 'secondary' }"
              is-rounded
              size="32"
              @click="$emit('scroll-button-click')"
            >
              <div class="d-flex align-center gap-1">
                <widget-icon
                  name="arrow_downward"
                  size="20px"
                />
                {{ scrollButtonText }}
              </div>
            </widget-button>
          </div>
        </transition>
      </div>

      <div class="chat-wrapper__footer">
        <div
          v-if="isChatEndedState(chatState)"
          class="pb-4 d-flex flex-column gap-2"
        >
          <widget-button
            v-if="isCloseThisWindowButtonShown"
            v-bind="themeButtonProps"
            class="full-width"
            @click="$emit('close-window')"
          >
            {{ $t('chat.closeThisWindow') }}
          </widget-button>

          <widget-button
            v-if="isStartNewChatButtonShown"
            v-bind="themeButtonProps"
            class="full-width"
            :view="isCloseThisWindowButtonShown ? 'outline' : 'solid'"
            @click="$emit('start-new-chat')"
          >
            {{ $t('chat.startNewChat') }}
          </widget-button>

          <widget-button
            v-if="isLeaveFeedbackButtonShown"
            v-bind="themeButtonProps"
            class="full-width"
            view="outline"
            @click="$emit('leave-feedback')"
          >
            {{ $t('chat.leaveFeedback') }}
          </widget-button>
        </div>
        <widget-form-field-content-editor
          v-else
          name="message"
          attachment-field-name="attachments"
          autofocus
          autofocus-after-submit
          :placeholder="$t('chat.typeYourMessageHere')"
          :is-loading="isChatLoadingState(chatState)"
          :max-length="messengerMessageMaxLength"
          @submit="$emit('submit')"
          @files-picked="$emit('files-picked', $event)"
          @remove-attachment="$emit('remove-attachment', $event)"
          @retry-attachment="$emit('retry-attachment', $event)"
        />
      </div>
    </div>
    <widget-file-drop-zone
      class="chat-drop-zone"
      :class="{
        'chat-drop-zone--active': isDragOver,
      }"
      :text="$t('common.dragAndDropToSend')"
      :max-size="maxFileSize"
    />
  </messenger-chat-view-layout>
</template>

<script setup lang="ts">
import type { MessengerChatViewSettings } from '@widgets/messenger/types'
import MessengerChatViewLayout from '@widgets/messenger/components/MessengerChatViewLayout.vue'
import { useWidgetChatViewSizeObserver } from '@widgets/messenger/compositions/useWidgetChatViewSizeObserver'
import WidgetFormFieldContentEditor from '@widgets/shared/components/fields/WidgetFormFieldContentEditor.vue'
import MessengerChatMessageList from '@widgets/messenger/components/MessengerChatMessageList.vue'
import {
  isChatCommentedState,
  isChatEndedState,
  isChatLoadingState,
  isChatNoAgentsState,
  isChatOfflineState,
  isChatWaitingState,
  type MessengerChatState,
  type MessengerMessageListState,
  type MessengerNewMessageState,
} from '@widgets/messenger/types/chat/chat'
import type { MessengerMessageListItem } from '@widgets/messenger/types/chat/message'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import { scrollbarDataIdAttribute } from '@widgets/shared/const'
import translateService from '@widgets/shared/services/translate/translateService'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import { messengerMessageMaxLength } from '@widgets/messenger/const'
import { useDragAndDropFiles } from '@widgets/messenger/compositions/useDragAndDropFiles'
import WidgetFileDropZone from '@widgets/shared/components/WidgetFileDropZone.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { computed, ref, watchEffect } from '@/composition/vue/compositionApi'

const props = defineProps<{
  viewSettings: MessengerChatViewSettings
  chatState: MessengerChatState
  agentTypingCount: number
  messageListItems: MessengerMessageListItem[]
  messageListState: MessengerMessageListState
  isSatisfactionSurveyShown: boolean
  maxFileSize: number // in bytes
  scrollBarId?: string
  newMessageDividerId?: string
  newMessageState?: MessengerNewMessageState
  isScrollButtonShown?: boolean
  numberOfNewMessages?: number
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'end-chat'): void
  (e: 'resize', height: number): void
  (e: 'submit'): void
  (e: 'start-new-chat'): void
  (e: 'leave-feedback'): void
  (e: 'close-window'): void
  (e: 'scroll-button-click'): void
  (e: 'files-picked', files: File[]): void
  (e: 'remove-attachment', attachmentId: string): void
  (e: 'retry-attachment', attachmentId: string): void
  (e: 'load-next-page'): void
}>()

const viewRef = ref<HTMLElement | null>(null)
const viewScrollContainerRef = ref<HTMLElement | null>(null)
const viewScrolledContentRef = ref<HTMLElement | null>(null)

const { themeButtonProps } = useWidgetTheme()

const { height } = useWidgetChatViewSizeObserver({
  viewRef,
  viewScrollContainerRef,
  viewScrolledContentRef,
})
const { dropZoneProps, isDragOver } = useDragAndDropFiles((files) => {
  emit('files-picked', files)
})

const isNoAgents = computed(() => {
  return isChatNoAgentsState(props.chatState)
})

const isSurveyCompleted = computed(() => {
  return isChatCommentedState(props.chatState)
})

const isOfflineMode = computed(() => {
  return isChatOfflineState(props.chatState)
})

const isCloseThisWindowButtonShown = computed(() => {
  return isNoAgents.value || isOfflineMode.value
})

const isStartNewChatButtonShown = computed(() => {
  return !isOfflineMode.value
})

const isLeaveFeedbackButtonShown = computed(() => {
  return !isNoAgents.value && props.isSatisfactionSurveyShown && !isSurveyCompleted.value && !isOfflineMode.value
})

watchEffect(() => {
  emit('resize', height.value)
})

const hasNotViewedNewMessages = computed(() => props.newMessageState && !props.newMessageState.isViewed)
const scrollButtonText = computed(() => {
  if (hasNotViewedNewMessages.value) {
    const numberOfNewMessages = props.numberOfNewMessages || 0
    if (numberOfNewMessages === 1) {
      return translateService.t('chat.newMessage')
    }
    return translateService.t('chat.newMessages', {
      count: numberOfNewMessages > 9 ? '9+' : numberOfNewMessages,
    })
  }
  return translateService.t('chat.goToLatestMessage')
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

$desiredChatHeight: 400px;
$sidePadding: 16px;
$dropZonePadding: 16px;

.chat-wrapper {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-height: 0;

  &__chat-area {
    position: relative;
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: $desiredChatHeight;
    max-height: $desiredChatHeight;
    min-height: 0;
  }

  &__scroll-container {
    overflow: auto;
    height: 100%;
  }

  &__scrolled-content {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: end;
  }

  &__scroll-button {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__footer {
    flex-shrink: 0;
    flex-grow: 0;
    padding: 0 $sidePadding;
  }
}

.chat-drop-zone {
  position: absolute;
  top: $dropZonePadding;
  left: $dropZonePadding;
  width: calc(100% - 2 * $dropZonePadding);
  height: calc(100% - $dropZonePadding);
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  transition: opacity $transition-200ms-ease-in-out;

  &--active {
    opacity: 1;
    pointer-events: all;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-100ms-ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
